// src/components/CalculatorLoader.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';

const TALL_CALCULATORS = [
  'ai-usage-score-calculator',
  'fitness-calculator',
  'longevity-lifestyle-calculator',
  'study-efficiency-calculator',
  'ai-tutor-cost-comparison'
];

const CalculatorLoader = ({ calculatorSlug }) => {
  const [calculator, setCalculator] = useState(null);
  const [calculatorFile, setCalculatorFile] = useState(null);
  const [fileExists, setFileExists] = useState(true);
  const [loading, setLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    const loadCalculator = async () => {
      try {
        setLoading(true);

        // 1. Fetch calculator metadata from API
        const response = await apiService.getCalculator(calculatorSlug);
        if (!response.success || !response.data) {
          throw new Error('Calculator not found in API');
        }
        setCalculator(response.data);

        // 2. Once API succeeds, set the file path
        const file = calculatorSlug + '.html';
        setCalculatorFile(file);
        setFileExists(true);
      } catch (error) {
        console.error('Failed to load calculator:', error);
        setFileExists(false);
      } finally {
        setLoading(false);
      }
    };

    loadCalculator();
  }, [calculatorSlug]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto neon-glow"></div>
        <p className="mt-4 font-label-caps text-xs text-outline tracking-widest uppercase">Initializing_Compute_Core...</p>
      </div>
    );
  }

  if (!fileExists || !calculator) {
    return (
      <div className="text-center py-16 glass-card rounded-lg border-primary/20 mx-4">
        <div className="text-primary text-6xl mb-6 neon-glow group">
           <span className="material-symbols-outlined text-6xl">construction</span>
        </div>
        <h3 className="font-headline-md text-primary mb-2 uppercase tracking-tighter">Node_Under_Construction</h3>
        <p className="font-body-md text-on-surface-variant max-w-sm mx-auto">This calculation engine is currently undergoing neural calibration. Check back shortly.</p>
        <div className="mt-12 flex justify-center">
            <AdBanner adKey="857d3637cfc0b322e60f1c5c2a864a62" width={320} height={50} />   
        </div>  
      </div>
    );
  }

  const keywordsArray = calculator.keywords?.split(",").map(keyword => keyword.trim()) || [];
  const keywordsString =  keywordsArray.length > 0 ? keywordsArray.join(', ') : '';

  // Force dark mode as the app is now dark-only
  const iframeUrl = `/calc-files/${calculatorFile}?dark=true`;
  const heightClass = TALL_CALCULATORS.includes(calculatorSlug) 
    ? "min-h-[2100px]" 
    : "min-h-[1270px]";

  return (
    <div className="w-full container rounded-lg overflow-hidden">
      <SEO
        title={calculator.name}
        description={calculator.meta_description}
        keywords={keywordsString}
        toolType="calculator"
      />
      <iframe
        key={iframeKey}
        src={iframeUrl}
        className={`w-full ${heightClass} border-0`}
        title={`${calculatorSlug} Calculator`}
        loading="lazy"
        onError={() => setFileExists(false)}
      />
    </div>
  );
};

export default CalculatorLoader;